import { useState, useEffect, useRef, useMemo } from 'react';
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
    ArrowLeft,
    Download,
    Upload,
    Loader2,
    XCircle,
    CheckCircle,
    FileSpreadsheet,
    Table as TableIcon,
    History,
    Search,
    Filter,
    X,
    Plus,
    Pencil,
    Trash2
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { metadataAPI } from '../services/api';
import { ConfirmModal } from '../components/ui/confirm-modal';

/** Human-readable labels for each metadata sheet type. */
const SHEET_TYPE_LABELS = {
    project: 'Project Metadata',
    batch: 'Batch',
    master: 'Master Metadata',
};

/**
 * VersionHistory
 *
 * Fetches and displays the version history for a metadata sheet.
 *
 * @param {number} sheetId - The ID of the sheet whose history to display.
 */
function VersionHistory({ sheetId }) {
    const [versions, setVersions] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetch = async () => {
            try {
                setLoading(true);
                const res = await metadataAPI.getVersions(sheetId);
                setVersions(res.data);
            } catch {
            } finally {
                setLoading(false);
            }
        };
        fetch();
    }, [sheetId]);

    if (loading) return <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />;
    if (versions.length === 0) return null;

    return (
        <div className="space-y-2">
            <h4 className="text-sm font-medium flex items-center gap-1.5">
                <History className="h-4 w-4" />
                Version History
            </h4>
            <div className="space-y-1">
                {versions.map((v) => (
                    <div key={v.version} className="flex items-center gap-3 text-xs text-muted-foreground">
                        <span className="font-mono">v{v.version}</span>
                        <span>{v.change_description || '—'}</span>
                        <span className="ml-auto">
                            {new Date(v.created_at).toLocaleDateString()}
                        </span>
                    </div>
                ))}
            </div>
        </div>
    );
}

/**
 * applyFilter — evaluates a single column filter rule against a cell value.
 */
function applyFilter(cellValue, operator, filterValue) {
    const cell = String(cellValue ?? '').toLowerCase();
    const target = String(filterValue ?? '').toLowerCase();
    switch (operator) {
        case 'contains':     return cell.includes(target);
        case 'not_contains': return !cell.includes(target);
        case 'equals':       return cell === target;
        case 'starts_with':  return cell.startsWith(target);
        default:             return true;
    }
}

/**
 * MetadataViewer
 *
 * Route: /project/:projectId/metadata
 *
 * Full-screen spreadsheet viewer. Sheet selection tabs appear at the top
 * (Excel-style), the table fills all remaining height, and horizontal
 * overflow is handled by the table's own scroll container.
 */
export default function MetadataViewer() {
    const navigate = useNavigate();
    const { projectId } = useParams();
    const location = useLocation();

    const [sheets, setSheets] = useState([]);
    const [activeSheetId, setActiveSheetId] = useState(null);
    const [sheetData, setSheetData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [sheetLoading, setSheetLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [uploading, setUploading] = useState(false);
    const fileInputRef = useRef(null);
    const [uploadTarget, setUploadTarget] = useState(null);
    const [confirmReplaceOpen, setConfirmReplaceOpen] = useState(false);
    const [pendingUploadTarget, setPendingUploadTarget] = useState(null);
    const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [filters, setFilters] = useState([]);
    const [filterPanelOpen, setFilterPanelOpen] = useState(false);
    const [draft, setDraft] = useState({ columnKey: '', operator: 'contains', value: '' });

    // Fetch sheet list — called on mount and on editor return.
    // If a sheet ID was requested via router state, activate it; otherwise default to first.
    const fetchSheets = async () => {
        try {
            setLoading(true);
            const res = await metadataAPI.listByProject(projectId);
            const data = res.data;
            setSheets(data);
            if (data.length > 0 && !activeSheetId) {
                const preferred = requestedSheetId && data.find(s => s.id === requestedSheetId);
                setActiveSheetId(preferred ? preferred.id : data[0].id);
            }
        } catch (err) {
            setError(err.response?.data?.detail || 'Failed to load metadata');
        } finally {
            setLoading(false);
        }
    };

    // If a specific sheet was requested via router state (e.g. from MetadataDatabase "View" button),
    // apply it as the initial active sheet after the list loads.
    const requestedSheetId = location.state?.activeSheetId ?? null;

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { fetchSheets(); }, [projectId]);

    // Re-fetch after navigating back from the editor.
    useEffect(() => {
        if (location.state?.refresh) {
            fetchSheets();
            fetchActiveSheet(activeSheetId);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.state]);

    const fetchActiveSheet = async (sheetId) => {
        if (!sheetId) return;
        try {
            setSheetLoading(true);
            const res = await metadataAPI.getSheet(sheetId);
            setSheetData(res.data);
        } catch (err) {
            setError(err.response?.data?.detail || 'Failed to load sheet data');
        } finally {
            setSheetLoading(false);
        }
    };

    // Reset search/filters and load new sheet data when the active tab changes.
    useEffect(() => {
        if (!activeSheetId) return;
        setSearchQuery('');
        setFilters([]);
        fetchActiveSheet(activeSheetId);
    }, [activeSheetId]);

    const handleDownload = async (sheetId) => {
        try {
            const res = await metadataAPI.exportCSV(sheetId);
            const blob = new Blob([res.data], { type: 'text/csv' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            const sheet = sheets.find((s) => s.id === sheetId);
            link.href = url;
            link.download = `${sheet?.name || 'metadata'}.csv`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch {
            setError('Failed to download CSV');
            setTimeout(() => setError(''), 5000);
        }
    };

    const handleUploadClick = (metadataType, batchIndex) => {
        setPendingUploadTarget({ metadataType, batchIndex });
        setConfirmReplaceOpen(true);
    };

    const handleConfirmReplace = () => {
        setConfirmReplaceOpen(false);
        setUploadTarget(pendingUploadTarget);
        setPendingUploadTarget(null);
        fileInputRef.current?.click();
    };

    const handleCancelReplace = () => {
        setConfirmReplaceOpen(false);
        setPendingUploadTarget(null);
    };

    const handleFileSelected = async (e) => {
        const file = e.target.files?.[0];
        if (!file || !uploadTarget) return;
        try {
            setUploading(true);
            setError('');
            await metadataAPI.uploadCSV(projectId, uploadTarget.metadataType, file, uploadTarget.batchIndex);
            setSuccess('Metadata uploaded successfully');
            setTimeout(() => setSuccess(''), 3000);
            const res = await metadataAPI.listByProject(projectId);
            setSheets(res.data);
            if (activeSheetId) {
                const sheetRes = await metadataAPI.getSheet(activeSheetId);
                setSheetData(sheetRes.data);
            }
        } catch (err) {
            setError(err.response?.data?.detail || 'Failed to upload metadata');
            setTimeout(() => setError(''), 5000);
        } finally {
            setUploading(false);
            setUploadTarget(null);
            e.target.value = '';
        }
    };

    const handleDeleteSheet = async () => {
        if (!activeSheetId) return;
        try {
            setDeleting(true);
            setError('');
            await metadataAPI.deleteSheet(activeSheetId);
            // Remove the deleted sheet from the list and activate the next available one.
            const remaining = sheets.filter(s => s.id !== activeSheetId);
            setSheets(remaining);
            setSheetData(null);
            setActiveSheetId(remaining.length > 0 ? remaining[0].id : null);
            setSuccess('Sheet deleted successfully');
            setTimeout(() => setSuccess(''), 3000);
        } catch (err) {
            setError(err.response?.data?.detail || 'Failed to delete sheet');
            setTimeout(() => setError(''), 5000);
        } finally {
            setDeleting(false);
            setConfirmDeleteOpen(false);
        }
    };

    // Sort sheets: batches in order, then master.
    const sortedSheets = [...sheets].sort((a, b) => {
        const typeOrder = { project: 0, batch: 1, master: 2 };
        const aOrder = typeOrder[a.type] ?? 1;
        const bOrder = typeOrder[b.type] ?? 1;
        if (aOrder !== bOrder) return aOrder - bOrder;
        return (a.batch_index ?? 0) - (b.batch_index ?? 0);
    });

    const activeSheet = sheets.find((s) => s.id === activeSheetId);

    // Client-side filtering — recalculates only when inputs change.
    const filteredRows = useMemo(() => {
        let rows = sheetData?.rows || [];
        const cols = sheetData?.columns || [];
        if (searchQuery.trim()) {
            const query = searchQuery.toLowerCase();
            rows = rows.filter(row =>
                cols.some(col => String(row[col.key] ?? '').toLowerCase().includes(query))
            );
        }
        for (const f of filters) {
            rows = rows.filter(row => applyFilter(row[f.columnKey], f.operator, f.value));
        }
        return rows;
    }, [sheetData, searchQuery, filters]);

    const removeFilter = (indexToRemove) =>
        setFilters(filters.filter((_, i) => i !== indexToRemove));

    const clearFilters = () => setFilters([]);

    const addFilter = () => {
        if (!draft.columnKey || !draft.value.trim()) return;
        setFilters(prev => [...prev, { ...draft }]);
        setDraft({ columnKey: '', operator: 'contains', value: '' });
    };

    // Tab label helper.
    const sheetLabel = (sheet) =>
        sheet.type === 'batch'
            ? `Batch ${sheet.batch_index}`
            : SHEET_TYPE_LABELS[sheet.type] || sheet.name;

    return (
        <>
        <div className="h-screen flex flex-col bg-background overflow-hidden">

            {/* ── Top header bar ──────────────────────────────────────────── */}
            <header className="border-b bg-card shrink-0">
                <div className="flex items-center justify-between px-4 py-3 gap-4">
                    <div className="flex items-center gap-3 min-w-0">
                        <Link to="/metadata">
                            <Button variant="ghost" size="sm">
                                <ArrowLeft className="h-4 w-4 mr-1.5" />
                                Metadata
                            </Button>
                        </Link>
                        <div className="min-w-0">
                            <h1 className="text-xl font-bold flex items-center gap-2 truncate">
                                <FileSpreadsheet className="h-5 w-5 shrink-0" />
                                Project #{projectId} — Metadata
                            </h1>
                        </div>
                    </div>
                    <Button
                        size="sm"
                        onClick={() => navigate(`/project/${projectId}/metadata/new`)}
                    >
                        <Plus className="h-4 w-4 mr-1.5" />
                        New Sheet
                    </Button>
                </div>

                {/* ── Sheet tabs ──────────────────────────────────────────── */}
                {!loading && sheets.length > 0 && (
                    <div className="flex overflow-x-auto border-t px-2 gap-0.5 bg-muted/30">
                        {sortedSheets.map((sheet) => {
                            const isActive = sheet.id === activeSheetId;
                            return (
                                <button
                                    key={sheet.id}
                                    onClick={() => setActiveSheetId(sheet.id)}
                                    className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                                        isActive
                                            ? 'border-primary text-foreground bg-background'
                                            : 'border-transparent text-muted-foreground hover:text-foreground hover:bg-muted/50'
                                    }`}
                                >
                                    <FileSpreadsheet className="h-3.5 w-3.5 shrink-0" />
                                    {sheetLabel(sheet)}
                                    <span className={`text-xs ${isActive ? 'text-muted-foreground' : 'text-muted-foreground/60'}`}>
                                        {sheet.row_count}
                                    </span>
                                </button>
                            );
                        })}
                    </div>
                )}
            </header>

            {/* ── Notifications ───────────────────────────────────────────── */}
            {(success || error) && (
                <div className="shrink-0 px-4 pt-3">
                    {success && (
                        <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }}
                            className="flex items-center gap-2 text-sm text-green-800 dark:text-green-200 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 px-3 py-2 rounded-lg"
                        >
                            <CheckCircle className="h-4 w-4 shrink-0" />
                            {success}
                        </motion.div>
                    )}
                    {error && (
                        <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }}
                            className="flex items-center gap-2 text-sm text-destructive bg-destructive/10 border border-destructive/20 px-3 py-2 rounded-lg"
                        >
                            <XCircle className="h-4 w-4 shrink-0" />
                            {error}
                        </motion.div>
                    )}
                </div>
            )}

            {/* ── Main content ────────────────────────────────────────────── */}
            <main className="flex-1 min-h-0 flex flex-col overflow-hidden">

                <input ref={fileInputRef} type="file" accept=".csv" className="hidden" onChange={handleFileSelected} />

                {/* Loading */}
                {loading && (
                    <div className="flex items-center justify-center py-12">
                        <Loader2 className="h-8 w-8 animate-spin text-primary" />
                        <span className="ml-3 text-muted-foreground">Loading metadata...</span>
                    </div>
                )}

                {/* Empty state */}
                {!loading && !error && sheets.length === 0 && (
                    <div className="flex flex-col items-center justify-center flex-1 gap-4">
                        <FileSpreadsheet className="h-12 w-12 text-muted-foreground" />
                        <h3 className="text-lg font-medium">No metadata yet</h3>
                        <p className="text-muted-foreground text-sm">Create a new sheet or upload a CSV file.</p>
                        <Button onClick={() => navigate(`/project/${projectId}/metadata/new`)}>
                            <Plus className="h-4 w-4 mr-1.5" />
                            New Sheet
                        </Button>
                    </div>
                )}

                {/* Sheet content */}
                {!loading && sheets.length > 0 && sheetLoading && (
                    <div className="flex items-center justify-center py-12">
                        <Loader2 className="h-6 w-6 animate-spin text-primary" />
                        <span className="ml-3 text-muted-foreground">Loading sheet...</span>
                    </div>
                )}

                {!loading && !sheetLoading && sheetData && (
                    <motion.div
                        key={activeSheetId}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.12 }}
                        className="flex-1 min-h-0 flex flex-col overflow-hidden"
                    >
                        {/* Sheet toolbar — actions + search */}
                        <div className="shrink-0 px-4 py-3 border-b flex flex-col gap-3 bg-card">
                            <div className="flex items-center justify-between gap-3">
                                {/* Sheet meta */}
                                <div>
                                    <div className="flex items-center gap-2">
                                        <TableIcon className="h-4 w-4 text-muted-foreground" />
                                        <span className="font-semibold text-sm">
                                            {activeSheet?.type === 'batch'
                                                ? `Batch ${activeSheet.batch_index} Metadata`
                                                : SHEET_TYPE_LABELS[activeSheet?.type] || sheetData.name}
                                        </span>
                                        {sheetData.version > 1 && (
                                            <span className="text-xs text-muted-foreground">v{sheetData.version}</span>
                                        )}
                                    </div>
                                    <p className="text-xs text-muted-foreground mt-0.5">
                                        {sheetData.column_count} columns ·{' '}
                                        {searchQuery
                                            ? `${filteredRows.length} of ${sheetData.row_count}`
                                            : sheetData.row_count} rows
                                        {sheetData.updated_at && ` · Updated ${new Date(sheetData.updated_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`}
                                    </p>
                                </div>
                                {/* Action buttons */}
                                <div className="flex gap-2 shrink-0">
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => navigate(`/project/${projectId}/metadata/${activeSheetId}/edit`)}
                                    >
                                        <Pencil className="h-4 w-4 mr-1.5" />
                                        Edit
                                    </Button>
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => handleDownload(activeSheetId)}
                                    >
                                        <Download className="h-4 w-4 mr-1.5" />
                                        Download
                                    </Button>
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => handleUploadClick(activeSheet?.type, activeSheet?.batch_index)}
                                        disabled={uploading}
                                    >
                                        {uploading
                                            ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                                            : <Upload className="h-4 w-4 mr-1.5" />}
                                        Replace CSV
                                    </Button>
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => setConfirmDeleteOpen(true)}
                                        className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950"
                                    >
                                        <Trash2 className="h-4 w-4 mr-1.5" />
                                        Delete
                                    </Button>
                                </div>
                            </div>

                            {/* Search + filter row */}
                            <div className="flex gap-2">
                                <div className="relative flex-1">
                                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
                                    <input
                                        type="text"
                                        placeholder="Search all rows..."
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                        className="w-full pl-9 pr-4 py-2 text-sm border rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-ring"
                                    />
                                </div>
                                <Button variant="outline" size="sm" onClick={() => setFilterPanelOpen(p => !p)}>
                                    <Filter className="h-4 w-4 mr-1.5" />
                                    Filter
                                    {filters.length > 0 && (
                                        <span className="ml-1.5 bg-primary text-primary-foreground text-xs rounded-full px-1.5 py-0.5">
                                            {filters.length}
                                        </span>
                                    )}
                                </Button>
                            </div>

                            {/* Active filter tags */}
                            {filters.length > 0 && (
                                <div className="flex gap-2 flex-wrap items-center">
                                    <span className="text-xs text-muted-foreground">Active filters:</span>
                                    {filters.map((f, index) => {
                                        const col = sheetData.columns.find(c => c.key === f.columnKey);
                                        return (
                                            <span key={index} className="flex items-center gap-1 text-xs bg-muted px-2 py-1 rounded-full">
                                                <span className="font-medium">{col?.label ?? f.columnKey}</span>
                                                <span className="text-muted-foreground">{f.operator.replace('_', ' ')}</span>
                                                <span>"{f.value}"</span>
                                                <Button variant="ghost" size="sm" className="ml-1 h-4 w-4 p-0 hover:text-destructive" onClick={() => removeFilter(index)}>
                                                    <X className="h-3 w-3" />
                                                </Button>
                                            </span>
                                        );
                                    })}
                                    <Button variant="ghost" size="sm" className="text-xs text-muted-foreground" onClick={clearFilters}>
                                        Clear all
                                    </Button>
                                </div>
                            )}

                            {/* Filter builder */}
                            {filterPanelOpen && (
                                <div className="flex gap-2 flex-wrap">
                                    <select
                                        value={draft.columnKey}
                                        onChange={(e) => setDraft(p => ({ ...p, columnKey: e.target.value }))}
                                        className="text-sm border rounded-md px-2 py-1.5 bg-background"
                                    >
                                        <option value="">Column...</option>
                                        {sheetData.columns.map(col => (
                                            <option key={col.key} value={col.key}>{col.label}</option>
                                        ))}
                                    </select>
                                    <select
                                        value={draft.operator}
                                        onChange={(e) => setDraft(p => ({ ...p, operator: e.target.value }))}
                                        className="text-sm border rounded-md px-2 py-1.5 bg-background"
                                    >
                                        <option value="contains">contains</option>
                                        <option value="not_contains">not contains</option>
                                        <option value="equals">equals</option>
                                        <option value="starts_with">starts with</option>
                                    </select>
                                    <input
                                        type="text"
                                        placeholder="Value..."
                                        value={draft.value}
                                        onChange={(e) => setDraft(p => ({ ...p, value: e.target.value }))}
                                        onKeyDown={(e) => e.key === 'Enter' && addFilter()}
                                        className="text-sm border rounded-md px-2 py-1.5 bg-background flex-1 min-w-[120px]"
                                    />
                                    <Button size="sm" onClick={addFilter}>+ Add</Button>
                                </div>
                            )}
                        </div>

                        {/* Table — fills remaining screen height, scrolls both axes */}
                        <div className="flex-1 overflow-auto min-h-0">
                            <table className="w-full min-w-max text-sm">
                                <thead className="sticky top-0 z-10 bg-muted/95 backdrop-blur-sm">
                                    <tr className="border-b">
                                        <th className="h-10 px-4 text-center font-medium text-muted-foreground w-12 font-mono text-xs whitespace-nowrap">
                                            #
                                        </th>
                                        {sheetData.columns?.map((col) => (
                                            <th
                                                key={col.key}
                                                className="h-10 px-4 text-left font-medium text-muted-foreground whitespace-nowrap"
                                            >
                                                {col.label}
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredRows.map((row, rowIdx) => (
                                        <tr
                                            key={rowIdx}
                                            className="border-b last:border-0 hover:bg-muted/40 transition-colors"
                                        >
                                            <td className="px-4 py-2 text-center font-mono text-xs text-muted-foreground">
                                                {rowIdx + 1}
                                            </td>
                                            {sheetData.columns?.map((col) => (
                                                <td key={col.key} className="px-4 py-2 whitespace-nowrap">
                                                    {row[col.key] ?? ''}
                                                </td>
                                            ))}
                                        </tr>
                                    ))}
                                    {filteredRows.length === 0 && (
                                        <tr>
                                            <td
                                                colSpan={(sheetData.columns?.length || 0) + 1}
                                                className="px-4 py-8 text-center text-muted-foreground text-sm"
                                            >
                                                No rows match your search or filters.
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>

                        {/* Version history — shown when sheet has been saved more than once */}
                        {sheetData.version > 1 && (
                            <div className="shrink-0 border-t px-4 py-3 bg-muted/20">
                                <VersionHistory sheetId={activeSheetId} />
                            </div>
                        )}
                    </motion.div>
                )}

            </main>
        </div>

        <ConfirmModal
            open={confirmReplaceOpen}
            title="Replace metadata sheet?"
            description="This will overwrite the current sheet with the CSV you select. This cannot be undone."
            confirmLabel="Replace"
            destructive
            onConfirm={handleConfirmReplace}
            onCancel={handleCancelReplace}
        />

        <ConfirmModal
            open={confirmDeleteOpen}
            title="Delete this sheet?"
            description={`"${activeSheet?.type === 'batch' ? `Batch ${activeSheet?.batch_index} Metadata` : activeSheet?.name}" will be permanently deleted along with all its version history. This cannot be undone.`}
            confirmLabel={deleting ? 'Deleting...' : 'Delete'}
            destructive
            onConfirm={handleDeleteSheet}
            onCancel={() => setConfirmDeleteOpen(false)}
        />
        </>
    );
}
